import { Component, OnInit } from '@angular/core';
import { AuthService } from 'src/app/services/auth/auth.service';
import { AgenteConfig, CambioAgente, EstadoIA, IaService, ModeloCatalogo } from 'src/app/services/ia/ia.service';
import {
  errorDeIA, errorDeProveedor, esFechaReal, numeroONulo, vacio, fechaHora, puedeConfigurarIA, queHaceAgente, tituloAgente, usd,
} from 'src/app/services/ia/ia-formato';

/**
 * SOLAPA «AGENTES» — `console/ia/agentes`.
 *
 * 📌 En la fila vive sólo el interruptor, que GUARDA AL TOQUE: es un booleano y
 * es lo que se hace apurado. El modelo, el tope, el máximo de tokens y las
 * instrucciones adicionales se abren DEBAJO de la fila (en línea, nunca en un
 * modal) con un solo «Guardar»: son un ajuste, no cuatro acciones sueltas.
 * Copiado de `pasarela_consola/components/ia/{solapa-agentes,ajustes-agente}.tsx`.
 *
 * 📍 El modelo del agente se puede VACIAR (`modeloId: null`, CONTRATO §7.2): la
 * primera opción del desplegable es «el modelo por omisión».
 */
@Component({
  selector: 'app-ia-agentes',
  templateUrl: './ia-agentes.component.html',
  styleUrls: ['./ia-agentes.component.css']
})
export class IaAgentesComponent implements OnInit {

  cargando = true;
  error: string | null = null;
  agentes: AgenteConfig[] = [];
  estado: EstadoIA | null = null;
  catalogo: ModeloCatalogo[] = [];

  abierto: string | null = null;
  form: { modeloId: string; maxTokens: any; topeDiarioUsd: any; promptExtra: string } =
    { modeloId: '', maxTokens: '', topeDiarioUsd: '', promptExtra: '' };
  guardando = false;
  resultado: { ok: boolean; texto: string } | null = null;

  /** El agente cuyo interruptor se está guardando, y el error de la fila. */
  prendiendo: string | null = null;
  errorFila: { [agente: string]: string | null } = {};

  readonly tituloAgente = tituloAgente;
  readonly queHaceAgente = queHaceAgente;
  readonly usd = usd;
  readonly errorDeProveedor = errorDeProveedor;
  readonly fechaHora = fechaHora;
  readonly esFechaReal = esFechaReal;

  constructor(private _ia: IaService, public _auth: AuthService) { }

  ngOnInit(): void { this.cargar(); }

  get puedeEditar(): boolean { return puedeConfigurarIA(this._auth); }

  cargar = async (): Promise<void> => {
    this.cargando = true;
    this.error = null;
    try {
      // Los agentes mandan; el estado y el catálogo acompañan y, si fallan, la
      // tabla sale igual con «—» donde faltan.
      const [ag, est, cat] = await Promise.all([
        this._ia.getAgentes(),
        this._ia.getEstado().catch(() => null),
        this._ia.getCatalogo().catch(() => [] as ModeloCatalogo[]),
      ]);
      this.agentes = Array.isArray(ag) ? ag : [];
      this.estado = est;
      this.catalogo = Array.isArray(cat) ? cat : [];
    } catch (e: any) {
      this.agentes = [];
      this.error = errorDeIA(e);
    } finally {
      this.cargando = false;
    }
  };

  vivo = (a: AgenteConfig) => this.estado?.agentes?.[a.agente] || null;

  /** Prendido pero sin correr: lo único de esta lista que hay que mirar. */
  caido = (a: AgenteConfig): boolean => { const v = this.vivo(a); return !!v && v.habilitado && !v.operativo; };

  nombreModelo = (id: string | null | undefined): string => {
    if (!id) return '';
    return this.catalogo.find((m) => m.id === id)?.nombre || id;
  };

  /** El modelo que corre de verdad: el suyo, o el por omisión. */
  modeloEnUso = (a: AgenteConfig): string =>
    a.modeloId ? this.nombreModelo(a.modeloId)
      : `Por omisión${this.estado?.modeloPorOmision ? ` (${this.nombreModelo(this.estado.modeloPorOmision)})` : ''}`;

  modeloFueraDeCatalogo = (): boolean =>
    !!this.form.modeloId && !this.catalogo.some((m) => m.id === this.form.modeloId);

  prender = async (a: AgenteConfig, habilitado: boolean): Promise<void> => {
    this.prendiendo = a.agente;
    this.errorFila[a.agente] = null;
    try {
      await this._ia.guardarAgente(a.agente, { habilitado });
      a.habilitado = habilitado;
      this.estado = await this._ia.getEstado().catch(() => this.estado);
    } catch (e: any) {
      this.errorFila[a.agente] = errorDeIA(e, true);
    } finally {
      this.prendiendo = null;
    }
  };

  alternar = (a: AgenteConfig): void => {
    if (this.abierto === a.agente) { this.abierto = null; return; }
    this.abierto = a.agente;
    this.resultado = null;
    this.form = {
      modeloId: a.modeloId || '',
      maxTokens: a.maxTokens !== undefined && a.maxTokens !== null ? String(a.maxTokens) : '',
      topeDiarioUsd: a.topeDiarioUsd !== undefined && a.topeDiarioUsd !== null ? String(a.topeDiarioUsd) : '',
      promptExtra: a.promptExtra || '',
    };
  };

  get formValido(): boolean {
    const t = this.form.maxTokens;
    // El núcleo acepta de 50 a 8.000 (`rutas-admin.js`, PATCH /agentes/:agente).
    const okTokens = vacio(t) || (Number(t) >= 50 && Number(t) <= 8000 && Number.isInteger(Number(t)));
    const p = this.form.topeDiarioUsd;
    const okTope = vacio(p) || Number(p) >= 0;
    return okTokens && okTope;
  }

  guardar = async (a: AgenteConfig): Promise<void> => {
    if (this.guardando || !this.formValido) return;
    const tope = numeroONulo(this.form.topeDiarioUsd);
    const cambios: CambioAgente = {
      modeloId: this.form.modeloId || null,
      promptExtra: String(this.form.promptExtra || '').trim(),
      // Vacío = sin tope propio (null), nunca 0.
      topeDiarioUsd: tope === null ? null : Math.round(tope * 100) / 100,
    };
    // Vacío = no se toca: el núcleo no acepta null en `maxTokens`.
    const tokens = numeroONulo(this.form.maxTokens);
    if (tokens !== null) cambios.maxTokens = tokens;
    this.guardando = true;
    this.resultado = null;
    try {
      const r = await this._ia.guardarAgente(a.agente, cambios);
      Object.assign(a, r && typeof r === 'object' ? r : cambios);
      this.resultado = { ok: true, texto: 'Guardado. Tarda hasta 10 segundos en tomar efecto.' };
      this.estado = await this._ia.getEstado().catch(() => this.estado);
    } catch (e: any) {
      this.resultado = { ok: false, texto: errorDeIA(e, true) };
    } finally {
      this.guardando = false;
    }
  };
}
